import { Fragment } from 'react';
import { Column } from '@simplywallst/ui-core';
import { GalleryContainerRow, Image } from './styled';
import { useContentfulImage } from '@/hooks/useContentfulImage';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';
import Media from 'react-media';
import { useTheme } from '@simplywallst/ui-core';
import { Helmet } from 'react-helmet-async';

interface GalleryImage {
  title?: string;
  description?: string;
  url?: string;
}

interface Props {
  gallery: GalleryImage[];
  mobileGallery: {
    top: GalleryImage[];
    bottom: GalleryImage[];
  };
}

export const Gallery = ({ gallery, mobileGallery }: Props) => {
  const theme = useTheme();
  const { isMobile, isTablet } = useUserAgent();
  const topMobileImages = [
    {
      avif: useContentfulImage({
        url: mobileGallery.top[0].url,
        title: mobileGallery.top[0].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: mobileGallery.top[0].url,
        title: mobileGallery.top[0].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: mobileGallery.top[1].url,
        title: mobileGallery.top[1].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: mobileGallery.top[1].url,
        title: mobileGallery.top[1].title,
      }),
    },
  ];
  const bottomMobileImages = [
    {
      avif: useContentfulImage({
        url: mobileGallery.bottom[0].url,
        title: mobileGallery.bottom[0].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: mobileGallery.bottom[0].url,
        title: mobileGallery.bottom[0].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: mobileGallery.bottom[1].url,
        title: mobileGallery.bottom[1].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: mobileGallery.bottom[1].url,
        title: mobileGallery.bottom[1].title,
      }),
    },
  ];
  const desktopImages = [
    {
      avif: useContentfulImage({
        url: gallery[0].url,
        title: gallery[0].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery[0].url,
        title: gallery[0].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: gallery[1].url,
        title: gallery[1].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery[1].url,
        title: gallery[1].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: gallery[2].url,
        title: gallery[2].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery[2].url,
        title: gallery[2].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: gallery[3].url,
        title: gallery[3].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery[3].url,
        title: gallery[3].title,
      }),
    },
  ];

  return (
    <>
      <Helmet>
        {topMobileImages.map((item, i) => (
          <Fragment key={i}>
            <link rel="prefetch" href={item.avif?.src} />
            <link rel="prefetch" href={item.jpg?.src} />
          </Fragment>
        ))}
        {bottomMobileImages.map((item, i) => (
          <Fragment key={i}>
            <link rel="prefetch" href={item.avif?.src} />
            <link rel="prefetch" href={item.jpg?.src} />
          </Fragment>
        ))}
        {desktopImages.map((item, i) => (
          <Fragment key={i}>
            <link rel="prefetch" href={item.avif?.src} />
            <link rel="prefetch" href={item.jpg?.src} />
          </Fragment>
        ))}
      </Helmet>
      {gallery.length > 0 && (
        <Media
          defaultMatches={isMobile || isTablet}
          query={{ maxWidth: theme.mdMaxRaw }}
        >
          {(match) => {
            if (match) {
              return (
                <GalleryContainerRow>
                  <Column>
                    {topMobileImages.map((item, i) => (
                      <DeferRenderUntilIntersecting key={i}>
                        {() => (
                          <picture>
                            <source srcSet={item.avif?.src} type="image/avif" />
                            <Image src={item.jpg?.src} alt={item.jpg?.alt} />
                          </picture>
                        )}
                      </DeferRenderUntilIntersecting>
                    ))}
                  </Column>
                  <Column>
                    {bottomMobileImages.map((item, i) => (
                      <DeferRenderUntilIntersecting key={i}>
                        {() => (
                          <picture>
                            <source srcSet={item.avif?.src} type="image/avif" />
                            <Image src={item.jpg?.src} alt={item.jpg?.alt} />
                          </picture>
                        )}
                      </DeferRenderUntilIntersecting>
                    ))}
                  </Column>
                </GalleryContainerRow>
              );
            }
            return (
              <GalleryContainerRow>
                {desktopImages.map((item, i) => (
                  <Column colMd={3} key={i}>
                    <DeferRenderUntilIntersecting>
                      {() => (
                        <picture>
                          <source srcSet={item.avif?.src} type="image/avif" />
                          <Image src={item.jpg?.src} alt={item.jpg?.alt} />
                        </picture>
                      )}
                    </DeferRenderUntilIntersecting>
                  </Column>
                ))}
              </GalleryContainerRow>
            );
          }}
        </Media>
      )}
    </>
  );
};
